import { useDispatch, useReduxState } from 'hooks';
import api from 'api';
import { breakpoints } from 'styles';
import {
  DisplayFormattedDate,
  MoreOptions,
  NoEmailAddress,
  Pagination,
  Search,
  SendEmail,
  SortBy,
  User
} from 'components/common';
import {
  Box,
  BoxEmpty,
  BoxTable,
  BoxTableCell,
  BoxTableHeader,
  BoxTableRow
} from 'components/common/box';
import {
  CourseUserDeadline,
  CourseUserMandatory
} from 'components/courses/common';
import {
  ShareUsersAdd,
  ShareUsersRemove,
  ShareUsersRemoveAll,
  ShareUsersTargetSchedule
} from '.';

import type { EmailTemplateData } from 'shared-types';
import type { ApiDataHookUpdateHandler } from 'lib/api/useApiData';
import type { UpdateShareObject, ShareUser } from './Share';

export type UpdateShareUserObject = UpdateShareObject & {
  throwError?: boolean;
};

interface Props {
  emailTemplates: EmailTemplateData[];
  emailTemplatesDefault: EmailTemplateData;
  listOfUserIds: number[];
  loading: boolean;
  page: number;
  pages: number;
  sortBy: string;
  updateUsers: ApiDataHookUpdateHandler;
  updateUsersParams: (params: {
    page?: number;
    search?: string;
    sortBy?: string;
  }) => void;
  users: ShareUser[];
}

const ShareUsers = ({
  emailTemplates,
  emailTemplatesDefault,
  listOfUserIds,
  loading,
  page,
  pages,
  sortBy,
  updateUsers,
  updateUsersParams,
  users
}: Props) => {
  const { dispatch } = useDispatch();

  const {
    messages,
    pageState: { course },
    serverConfig: { userUseName }
  } = useReduxState();

  const handleSendEmail = async ({
    email,
    targetUserEmailAddress
  }: {
    email: EmailTemplateData['email'];
    targetUserEmailAddress: string;
  }) => {
    try {
      const data = {
        emailContent: email,
        emails: [targetUserEmailAddress],
        recipientsType: 'all'
      };

      const response = await api.post(
        `/admin/courses/${course.id}/users`,
        data
      );

      const errorEmails = Object.keys(response.data.errors);
      if (errorEmails && errorEmails.length) return false;

      dispatch.addNotice({
        text: messages.shareSendInviteEmailSuccess,
        type: 'success'
      });
    } catch (err) {
      await dispatch.handleErrorDispatch(err);
    }
  };

  const handleUserUpdates = async ({
    page,
    search,
    sortBy,
    throwError
  }: UpdateShareUserObject) => {
    try {
      if (page !== undefined || search !== undefined || sortBy !== undefined) {
        await updateUsersParams({
          page,
          search,
          sortBy
        });
      } else {
        await updateUsers();
      }
    } catch (err) {
      if (throwError) throw new Error(err);
      else await dispatch.handleErrorDispatch(err);
    }
  };

  const updateCourse = async () => {
    const courses = await api.get(`/admin/courses?id=${course.id}`);
    dispatch.updatePageState({ course: courses.data.courses[0] });
  };

  return (
    <div className="course-users">
      <Box
        headerContent={
          <div className="header">
            <div className="search">
              <Search
                onSearch={handleUserUpdates}
                placeholder={messages.settingsSearchForUser}
                size="header"
              />
            </div>

            <div className="add">
              {emailTemplates && emailTemplatesDefault ? (
                <ShareUsersAdd
                  emailTemplates={emailTemplates}
                  emailTemplatesDefault={emailTemplatesDefault}
                  updateCourse={updateCourse}
                  updateUsers={handleUserUpdates}
                  userIds={listOfUserIds}
                />
              ) : null}
            </div>

            <div className="sort">
              <SortBy
                onChange={handleUserUpdates}
                options={[
                  userUseName
                    ? {
                        text: messages.commonName,
                        value: 'name'
                      }
                    : {
                        text: messages.commonUsername,
                        value: 'username'
                      },
                  {
                    text: messages.commonAdded,
                    value: 'added'
                  },
                  {
                    text: messages.commonInviteEmail,
                    value: 'inviteEmailDate'
                  },
                  {
                    text: messages.usersLastLogin,
                    value: 'lastLogin'
                  },
                  {
                    text: messages.courseTargetSchedule,
                    value: 'targetSchedule'
                  },
                  {
                    text: messages.commonMandatory,
                    value: 'mandatory'
                  }
                ]}
                value={sortBy}
              />
            </div>

            <div className="more">
              <MoreOptions category="lighter">
                <ShareUsersRemoveAll
                  updateCourse={updateCourse}
                  updateUsers={updateUsers}
                />
              </MoreOptions>
            </div>
          </div>
        }
        headerContentWrap={true}
        loading={loading}
        padding={0}
        title={messages.tasksUsers}
        {...(course?.userAccess === 'guests'
          ? {
              disabledOverlayContent: messages.coursesGuestsOnlyWarning,
              disabledOverlayIcon: 'exclamation-triangle'
            }
          : {})}
      >
        {users.length ? (
          <BoxTable>
            <BoxTableHeader hideAtBreakpoint={breakpoints.medium}>
              <BoxTableCell>{messages.commonUser}</BoxTableCell>
              <BoxTableCell>{messages.commonAdded}</BoxTableCell>
              <BoxTableCell>{messages.commonInviteEmail}</BoxTableCell>
              <BoxTableCell>{messages.usersLastLogin}</BoxTableCell>
              <BoxTableCell>{messages.commonMandatory}</BoxTableCell>
              <BoxTableCell>{messages.courseTargetSchedule}</BoxTableCell>
              <BoxTableCell></BoxTableCell>
            </BoxTableHeader>

            {users.map((user) => (
              <BoxTableRow key={user.id}>
                <BoxTableCell cellBreakpoint={breakpoints.medium}>
                  <User enableProfileLink={true} user={user} />
                </BoxTableCell>

                <BoxTableCell
                  cellBreakpoint={breakpoints.medium}
                  icon="check-circle"
                  label={messages.commonAdded}
                >
                  <DisplayFormattedDate date={user.addedDate} />
                </BoxTableCell>

                <BoxTableCell
                  cellBreakpoint={breakpoints.medium}
                  emptyValue="-"
                  label={messages.commonInviteEmail}
                  {...(user.inviteEmailDate ? { icon: 'envelope' } : {})}
                >
                  {user.inviteEmailDate ? (
                    <DisplayFormattedDate date={user.inviteEmailDate} />
                  ) : user.email ? null : (
                    <NoEmailAddress />
                  )}
                </BoxTableCell>

                <BoxTableCell
                  cellBreakpoint={breakpoints.medium}
                  emptyValue="-"
                  icon="key"
                  iconStyle={{
                    fontSize: '1.1rem',
                    position: 'relative',
                    top: -1
                  }}
                  label={messages.usersLastLogin}
                >
                  {user.lastLogin ? (
                    <DisplayFormattedDate date={user.lastLogin} />
                  ) : null}
                </BoxTableCell>

                <BoxTableCell
                  cellBreakpoint={breakpoints.medium}
                  label={messages.commonMandatory}
                >
                  <CourseUserMandatory
                    mandatoryReasons={user.mandatoryReasons}
                  />
                </BoxTableCell>

                <BoxTableCell
                  cellBreakpoint={breakpoints.medium}
                  label={messages.courseTargetSchedule}
                >
                  <CourseUserDeadline
                    deadline={user.deadline}
                    isEdited={Boolean(user.courseSettings?.deadlineType)}
                    noStatus={true}
                    style={{ fontSize: '1.5rem' }}
                  />
                </BoxTableCell>

                <BoxTableCell
                  shrink={true}
                  verticalBreakpoint={breakpoints.medium}
                >
                  <MoreOptions styleContentButtons={false}>
                    {emailTemplates && emailTemplatesDefault && user.email ? (
                      <SendEmail
                        buttonIsDropdown={true}
                        buttonText={messages.commonSendInviteEmail}
                        emailTemplates={emailTemplates}
                        emailTemplatesDefault={emailTemplatesDefault}
                        id={`share-users-send-email-${user.id}`}
                        modalTitle={messages.commonSendInviteEmail}
                        onSubmit={handleSendEmail}
                        targetUser={user}
                      />
                    ) : null}

                    <ShareUsersTargetSchedule
                      course={course}
                      mandatoryReasons={user.mandatoryReasons}
                      updateUsers={handleUserUpdates}
                      userCourseSettings={user.courseSettings}
                      userId={user.id}
                    />

                    <ShareUsersRemove
                      targetUser={user}
                      updateCourse={updateCourse}
                      updateUsers={updateUsers}
                    />
                  </MoreOptions>
                </BoxTableCell>
              </BoxTableRow>
            ))}
          </BoxTable>
        ) : (
          <BoxEmpty text={messages.tasksNoUsers} />
        )}
      </Box>

      <Pagination
        currentPage={page}
        disabled={course?.userAccess === 'guests'}
        numberOfPages={pages}
        onPageChange={handleUserUpdates}
      />

      <style jsx>{`
        .header {
          align-items: center;
          display: flex;
        }
        .add,
        .more,
        .sort {
          margin-left: 10px;
        }
        @media (min-width: ${breakpoints.small}px) {
          .search :global(input) {
            min-width: 300px;
          }
        }
      `}</style>
    </div>
  );
};

export default ShareUsers;
